"use client";

import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import styles from "./DDSDropdownMenu.module.css";
import { useEventListener, useWatch } from "@web-monorepo/hooks";
import { getSite, logEvent } from "@classdojo/log-client";
import { IconChevronDown } from "@web-monorepo/dds-icons";
import { useCombinedRefs } from "../../../shared/hooks";

interface DDSDropdownMenuContextType {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentRef: React.RefObject<HTMLDivElement>;
  triggerRef: React.RefObject<HTMLButtonElement>;
}

const DDSDropdownMenuContext = React.createContext<DDSDropdownMenuContextType>({
  open: false,
  onOpenChange: () => {},
  contentRef: { current: null },
  triggerRef: { current: null },
});

export interface DDSDropdownMenuProps extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root> {
  /**
   * Optional name for automated product events.
   * When provided, the component will automatically log open/close events.
   * Event format: `{site}.dropdownMenu.{data-name}.open` and `{site}.dropdownMenu.{data-name}.close`
   * @see https://www.notion.so/classdojo/Automatic-Product-Events-for-Web-bfc580f10a914c3ba514e5ec20f8ef9e?pvs=4
   */
  "data-name"?: string;
  /**
   * Additional metadata to include with automated events.
   */
  "event-metadata"?: Record<string, unknown>;
  /**
   * Experiment identifiers to include with automated events.
   */
  experiments?: string[];
  onOpen?: () => void;
  onClose?: () => void;
}

function DDSDropdownMenu({
  modal = true,
  defaultOpen,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  onOpen,
  onClose,
  children,
  "data-name": dataName,
  "event-metadata": metadata,
  experiments,
  ...props
}: DDSDropdownMenuProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const isControlled = typeof controlledOpen !== "undefined";
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      setInternalOpen(newOpen);
      controlledOnOpenChange?.(newOpen);
      if (newOpen) {
        onOpen?.();
      } else {
        onClose?.();
      }
    },
    [controlledOnOpenChange, onOpen, onClose],
  );

  useWatch(
    isOpen,
    (newOpen, oldOpen) => {
      if (!dataName) return;
      // Skip the initial mount call (oldOpen is undefined on the first run, even
      // when defaultOpen is true) so we only log actual open/close transitions.
      if (oldOpen === undefined) return;
      logEvent({
        eventName: `${getSite()}.dropdownMenu.${dataName}.${newOpen ? "open" : "close"}`,
        automatedEvent: true,
        metadata,
        experiments,
      });
    },
    { immediate: true },
  );

  const contextValue = React.useMemo(
    () => ({
      open: isOpen,
      onOpenChange: handleOpenChange,
      contentRef,
      triggerRef,
    }),
    [isOpen, handleOpenChange],
  );

  return (
    <DDSDropdownMenuContext.Provider value={contextValue}>
      <DropdownMenuPrimitive.Root modal={modal} open={isOpen} onOpenChange={handleOpenChange} {...props}>
        {children}
      </DropdownMenuPrimitive.Root>
    </DDSDropdownMenuContext.Provider>
  );
}

type DDSDropdownMenuTriggerProps = React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger> & {
  asChild?: boolean;
  hideDisclosureIcon?: boolean;
};

const DDSDropdownMenuTrigger = React.forwardRef<HTMLButtonElement, DDSDropdownMenuTriggerProps>(
  ({ asChild = false, className, hideDisclosureIcon = false, children, ...props }, forwardedRef) => {
    const { triggerRef } = useDropdownMenuContext();
    const combinedRef = useCombinedRefs(triggerRef, forwardedRef);

    if (asChild) {
      // In asChild mode the caller supplies its own element (e.g. DDSButton,
      // DDSIconButton), which brings its own styling. Radix's Slot merges our
      // className onto that child, so applying `styles.trigger` here would layer
      // its visual reset (background/border/padding/color) on top of the child
      // and clobber it depending on CSS source order. The reset also has no
      // purpose in asChild mode — the disclosure chevron is only rendered in the
      // non-asChild branch below. So forward only the caller's className.
      return (
        <DropdownMenuPrimitive.Trigger ref={combinedRef} className={className} asChild {...props}>
          {children}
        </DropdownMenuPrimitive.Trigger>
      );
    }

    return (
      <DropdownMenuPrimitive.Trigger ref={combinedRef} className={`${styles.trigger} ${className ?? ""}`} {...props}>
        {children}
        {!hideDisclosureIcon && <IconChevronDown size="s" className={styles.triggerIcon} />}
      </DropdownMenuPrimitive.Trigger>
    );
  },
);

DDSDropdownMenuTrigger.displayName = "DDSDropdownMenuTrigger";

const DDSDropdownMenuGroup = DropdownMenuPrimitive.Group;

const DDSDropdownMenuPortal = DropdownMenuPrimitive.Portal;

function useDropdownMenuContext() {
  const context = React.useContext(DDSDropdownMenuContext);
  if (!context) {
    throw new Error("useDropdownMenuContext must be used within a DDSDropdownMenu");
  }
  return context;
}

const DDSDropdownMenuContent: React.FC<DropdownMenuPrimitive.DropdownMenuContentProps> = ({
  className,
  sideOffset = 4,
  ...props
}) => {
  const { contentRef, onOpenChange } = useDropdownMenuContext();

  useEventListener(
    "keydown",
    (keyEvent) => {
      if (keyEvent.key !== "Escape") return;
      if (!document.activeElement) return;

      if (contentRef.current?.contains(document.activeElement)) {
        keyEvent.stopPropagation();
        keyEvent.preventDefault();
        onOpenChange(false);
      }
    },
    globalThis.window,
    true,
  );

  return (
    <DDSDropdownMenuPortal>
      <DropdownMenuPrimitive.Content
        ref={contentRef}
        sideOffset={sideOffset}
        className={`${styles.content} ${className ?? ""}`}
        onFocusCapture={(e) => {
          // This prevents focus to the dialog from being captured when the dialog is opened.
          e.stopPropagation();
        }}
        {...props}
      />
    </DDSDropdownMenuPortal>
  );
};
DDSDropdownMenuContent.displayName = "DDSDropdownMenuContent";

/**
 * A menu item within DDSDropdownMenu.
 *
 * @example Basic usage with automatic event logging
 * ```tsx
 * <DDSDropdownMenuItem
 *   data-name="settings-option"
 *   onSelect={() => openSettings()}
 * >
 *   Settings
 * </DDSDropdownMenuItem>
 * ```
 *
 * @example With asChild for navigation links
 * ```tsx
 * <DDSDropdownMenuItem data-name="profile-link" asChild>
 *   <Link to="/profile">Profile</Link>
 * </DDSDropdownMenuItem>
 * ```
 */
const DDSDropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
    variant?: "default" | "destructive";
    selected?: boolean;
    /**
     * Optional name for automated product events.
     * When provided, the component will automatically log select events.
     * Event format: `{site}.dropdownMenuItem.{data-name}.select`
     */
    "data-name"?: string;
    /**
     * Additional metadata to include with automated events.
     */
    "event-metadata"?: Record<string, unknown>;
    /**
     * Experiment identifiers to include with automated events.
     */
    experiments?: string[];
  }
>(
  (
    {
      className,
      inset,
      variant = "default",
      selected,
      onSelect,
      "data-name": dataName,
      "event-metadata": metadata,
      experiments,
      ...props
    },
    ref,
  ) => {
    const handleSelect = React.useCallback(
      (event: Event) => {
        if (dataName) {
          logEvent({
            eventName: `${getSite()}.dropdownMenuItem.${dataName}.select`,
            automatedEvent: true,
            metadata,
            experiments,
          });
        }
        onSelect?.(event);
      },
      [dataName, metadata, experiments, onSelect],
    );

    return (
      <DropdownMenuPrimitive.Item
        ref={ref}
        data-name={dataName}
        className={`${styles.item} ${inset ? styles.inset : ""} ${className ?? ""} ${variant === "destructive" ? styles.destructive : ""} ${selected ? styles.selected : ""}`}
        onSelect={handleSelect}
        {...props}
      />
    );
  },
);
DDSDropdownMenuItem.displayName = "DDSDropdownMenuItem";

const DDSDropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={`${styles.label} ${inset ? styles.inset : ""} ${className ?? ""}`}
    {...props}
  />
));
DDSDropdownMenuLabel.displayName = "DDSDropdownMenuLabel";

const DDSDropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator ref={ref} className={`${styles.separator} ${className ?? ""}`} {...props} />
));
DDSDropdownMenuSeparator.displayName = "DDSDropdownMenuSeparator";

// only added the items we need, add the rest as needed
// the items that are commented out were originally added by ai
export {
  DDSDropdownMenu,
  DDSDropdownMenuTrigger,
  DDSDropdownMenuContent,
  DDSDropdownMenuItem,
  //   DDSDropdownMenuCheckboxItem,
  //   DDSDropdownMenuRadioItem,
  DDSDropdownMenuLabel,
  DDSDropdownMenuSeparator,
  //   DDSDropdownMenuShortcut,
  DDSDropdownMenuGroup,
  DDSDropdownMenuPortal,
  //   DDSDropdownMenuSub,
  //   DDSDropdownMenuSubContent,
  //   DDSDropdownMenuSubTrigger,
  //   DDSDropdownMenuRadioGroup,
};
